import { BankAccountDetails, BankAccountLocation } from '@40seas/entities/bank-account';
import { BankConnectivityProvider } from '@40seas/entities/bank-connectivity';
import { Buyer } from '@40seas/entities/buyers';
import { Country } from '@40seas/entities/common';
import { FinancialConnection, FinancialConnectionProviderType } from '@40seas/entities/financial-connections';
import { BECSPaymentMethod, PaymentMethod, SEPAPaymentMethod } from '@40seas/entities/payment-methods';
import { ValidationError } from '@40seas/errors';
import { Address, Currency, PaymentMethodType } from '@40seas/models';
import startCase from 'lodash/startCase';

export class BankConnectivityProvidersService {
    static creditcardOnlyCurrencies = [
        Currency.TRY,
        Currency.THB,
        Currency.PHP,
        Currency.VND,
        Currency.MYR,
        Currency.IDR,
        Currency.BDT,
        Currency.FJD,
        Currency.KGS,
        Currency.KZT,
        Currency.LKR,
        Currency.NPR,
        Currency.PKR,
        Currency.ISK,
        Currency.ALL,
        Currency.AMD,
        Currency.AZN,
        Currency.BAM,
        Currency.GEL,
        Currency.RSD,
        Currency.ILS,
        Currency.QAR,
        Currency.SAR,
        Currency.AOA,
        Currency.BWP,
        Currency.DZD,
        Currency.EGP,
        Currency.GMD,
        Currency.KES,
        Currency.LSL,
        Currency.MAD,
        Currency.MGA,
        Currency.MUR,
        Currency.MWK,
        Currency.NAD,
        Currency.NGN,
        Currency.RWF,
        Currency.SCR,
        Currency.TZS,
        Currency.XAF,
        Currency.XOF,
        Currency.ZMW,
        Currency.BBD,
        Currency.BOB,
        Currency.BSD,
        Currency.BZD,
        Currency.CLP,
        Currency.COP,
        Currency.CRC,
        Currency.DOP,
        Currency.GTQ,
        Currency.GYD,
        Currency.HNL,
        Currency.JMD,
        Currency.NIO,
        Currency.PEN,
        Currency.PYG,
        Currency.TTD,
        Currency.UYU,
        Currency.HUF,
        Currency.CNY,
        Currency.CHF,
        Currency.KRW,
        Currency.NOK,
        Currency.CZK,
        Currency.ZAR,
        Currency.BRL,
        Currency.JPY,
    ];

    static creditcardOnlyCurrenciesMap = new Map<Country, Currency>([
        [Country.TURKEY, Currency.TRY],
        [Country.THAILAND, Currency.THB],
        [Country.PHILIPPINES, Currency.PHP],
        [Country.VIETNAM, Currency.VND],
        [Country.MALAYSIA, Currency.MYR],
        [Country.INDONESIA, Currency.IDR],
        [Country.BANGLADESH, Currency.BDT],
        [Country.FIJI, Currency.FJD],
        [Country.KYRGYZSTAN, Currency.KGS],
        [Country.KAZAKHSTAN, Currency.KZT],
        [Country.SRI_LANKA, Currency.LKR],
        [Country.NEPAL, Currency.NPR],
        [Country.PAKISTAN, Currency.PKR],
        [Country.ICELAND, Currency.ISK],
        [Country.ALBANIA, Currency.ALL],
        [Country.ARMENIA, Currency.AMD],
        [Country.AZERBAIJAN, Currency.AZN],
        [Country.BOSNIA_AND_HERZEGOVINA, Currency.BAM],
        [Country.GEORGIA, Currency.GEL],
        [Country.SERBIA, Currency.RSD],
        [Country.ISRAEL, Currency.ILS],
        [Country.QATAR, Currency.QAR],
        [Country.SAUDI_ARABIA, Currency.SAR],
        [Country.ANGOLA, Currency.AOA],
        [Country.BOTSWANA, Currency.BWP],
        [Country.ALGERIA, Currency.DZD],
        [Country.EGYPT, Currency.EGP],
        [Country.GAMBIA, Currency.GMD],
        [Country.KENYA, Currency.KES],
        [Country.LESOTHO, Currency.LSL],
        [Country.MOROCCO, Currency.MAD],
        [Country.MADAGASCAR, Currency.MGA],
        [Country.MAURITIUS, Currency.MUR],
        [Country.MALAWI, Currency.MWK],
        [Country.NAMIBIA, Currency.NAD],
        [Country.NIGERIA, Currency.NGN],
        [Country.RWANDA, Currency.RWF],
        [Country.SEYCHELLES, Currency.SCR],
        [Country.TANZANIA, Currency.TZS],
        [Country.CENTRAL_AFRICAN_REPUBLIC, Currency.XAF],
        [Country.COTE_D_IVOIRE, Currency.XOF],
        [Country.ZAMBIA, Currency.ZMW],
        [Country.BARBADOS, Currency.BBD],
        [Country.BOLIVIA, Currency.BOB],
        [Country.BAHAMAS, Currency.BSD],
        [Country.BELIZE, Currency.BZD],
        [Country.CHILE, Currency.CLP],
        [Country.COLOMBIA, Currency.COP],
        [Country.COSTA_RICA, Currency.CRC],
        [Country.DOMINICAN_REPUBLIC, Currency.DOP],
        [Country.GUATEMALA, Currency.GTQ],
        [Country.GUYANA, Currency.GYD],
        [Country.HONDURAS, Currency.HNL],
        [Country.JAMAICA, Currency.JMD],
        [Country.NICARAGUA, Currency.NIO],
        [Country.PERU, Currency.PEN],
        [Country.PARAGUAY, Currency.PYG],
        [Country.TRINIDAD_AND_TOBAGO, Currency.TTD],
        [Country.URUGUAY, Currency.UYU],
        [Country.HUNGARY, Currency.HUF],
        [Country.CHINA, Currency.CNY],
        [Country.SWITZERLAND, Currency.CHF],
        [Country.SOUTH_KOREA, Currency.KRW],
        [Country.NORWAY, Currency.NOK],
        [Country.CZECH_REPUBLIC, Currency.CZK],
        [Country.SOUTH_AFRICA, Currency.ZAR],
        [Country.BRAZIL, Currency.BRL],
        [Country.JAPAN, Currency.JPY],
    ]);

    static countryToPaymentMethodTypeMapper = new Map<Country, PaymentMethodType>([
        [Country.UNITED_STATES, PaymentMethodType.ACH],
        [Country.CANADA, PaymentMethodType.ACSS],
        [Country.UNITED_KINGDOM, PaymentMethodType.BACS],
        [Country.AUSTRALIA, PaymentMethodType.BECS],
        [Country.GERMANY, PaymentMethodType.SEPA],
        [Country.FRANCE, PaymentMethodType.SEPA],
        [Country.ITALY, PaymentMethodType.SEPA],
        [Country.SPAIN, PaymentMethodType.SEPA],
        [Country.BELGIUM, PaymentMethodType.SEPA],
        [Country.NETHERLANDS, PaymentMethodType.SEPA],
        [Country.GUADELOUPE, PaymentMethodType.SEPA],
        [Country.REUNION, PaymentMethodType.SEPA],
        [Country.MARTINIQUE, PaymentMethodType.SEPA],
        [Country.SWITZERLAND, PaymentMethodType.SEPA],
        [Country.CZECH_REPUBLIC, PaymentMethodType.SEPA],
        [Country.IRELAND, PaymentMethodType.SEPA],
        [Country.CROATIA, PaymentMethodType.SEPA],
        [Country.SLOVENIA, PaymentMethodType.SEPA],
        [Country.SWEDEN, PaymentMethodType.SEPA],
        [Country.ROMANIA, PaymentMethodType.SEPA],
        [Country.GREECE, PaymentMethodType.SEPA],
        [Country.BULGARIA, PaymentMethodType.SEPA],
        [Country.LITHUANIA, PaymentMethodType.SEPA],
        [Country.DENMARK, PaymentMethodType.SEPA],
        [Country.AUSTRIA, PaymentMethodType.SEPA],
        [Country.CYPRUS, PaymentMethodType.SEPA],
        [Country.ESTONIA, PaymentMethodType.SEPA],
        [Country.FINLAND, PaymentMethodType.SEPA],
        [Country.HUNGARY, PaymentMethodType.SEPA],
        [Country.LATVIA, PaymentMethodType.SEPA],
        [Country.LIECHTENSTEIN, PaymentMethodType.SEPA],
        [Country.LUXEMBOURG, PaymentMethodType.SEPA],
        [Country.MONACO, PaymentMethodType.SEPA],
        [Country.NEW_ZEALAND, PaymentMethodType.BECS],
        [Country.NORWAY, PaymentMethodType.SEPA],
        [Country.POLAND, PaymentMethodType.SEPA],
        [Country.PORTUGAL, PaymentMethodType.SEPA],
        [Country.SAN_MARINO, PaymentMethodType.SEPA],
        [Country.SLOVAKIA, PaymentMethodType.SEPA],
    ]);

    static getBuyerDefaultFinancialConnectionProvider(buyer?: Buyer): BankConnectivityProvider | null {
        return this.getBuyersFinancialConnectionProviders(buyer)?.[0] || null;
    }

    static getFinancialConnectionSupportedCountriesByProvider(provider: BankConnectivityProvider): Country[] {
        switch (provider) {
            case BankConnectivityProvider.Stripe:
                return [Country.UNITED_STATES];
            case BankConnectivityProvider.Plaid:
                return [Country.UNITED_STATES, Country.CANADA];
            case BankConnectivityProvider.GoCardLess:
                return [
                    Country.UNITED_KINGDOM,
                    Country.GERMANY,
                    Country.FRANCE,
                    Country.ITALY,
                    Country.SPAIN,
                    Country.BELGIUM,
                    Country.NETHERLANDS,
                    Country.GUADELOUPE,
                    Country.REUNION,
                    Country.MARTINIQUE,
                    Country.IRELAND,
                    Country.DENMARK,
                    Country.CZECH_REPUBLIC,
                    Country.SLOVENIA,
                    Country.CROATIA,
                    Country.SWEDEN,
                    Country.GREECE,
                    Country.BULGARIA,
                    Country.ROMANIA,
                    Country.LITHUANIA,
                    Country.AUSTRIA,
                    Country.CYPRUS,
                    Country.ESTONIA,
                    Country.FINLAND,
                    Country.HUNGARY,
                    Country.ICELAND,
                    Country.LATVIA,
                    Country.LIECHTENSTEIN,
                    Country.LUXEMBOURG,
                    Country.MALTA,
                    Country.MONACO,
                    Country.NORWAY,
                    Country.POLAND,
                    Country.PORTUGAL,
                ];
            default:
                return [];
        }
    }

    static getBuyersFinancialConnectionProviders(buyer?: Buyer): BankConnectivityProvider[] | null {
        if (!buyer?.address?.country) {
            return null;
        }

        switch (buyer.address.country) {
            case Country.UNITED_STATES:
                return [BankConnectivityProvider.Stripe, BankConnectivityProvider.Plaid];
            case Country.CANADA:
                return [BankConnectivityProvider.Plaid];
            case Country.UNITED_KINGDOM:
            case Country.GERMANY:
            case Country.FRANCE:
            case Country.ITALY:
            case Country.SPAIN:
            case Country.BELGIUM:
            case Country.NETHERLANDS:
            case Country.GUADELOUPE:
            case Country.REUNION:
            case Country.MARTINIQUE:
            case Country.IRELAND:
            case Country.DENMARK:
            case Country.CZECH_REPUBLIC:
            case Country.SLOVENIA:
            case Country.CROATIA:
            case Country.SWEDEN:
            case Country.GREECE:
            case Country.BULGARIA:
            case Country.ROMANIA:
            case Country.LITHUANIA:
            case Country.AUSTRIA:
            case Country.CYPRUS:
            case Country.ESTONIA:
            case Country.FINLAND:
            case Country.HUNGARY:
            case Country.ICELAND:
            case Country.LATVIA:
            case Country.LIECHTENSTEIN:
            case Country.LUXEMBOURG:
            case Country.MALTA:
            case Country.MONACO:
            case Country.NORWAY:
            case Country.POLAND:
            case Country.PORTUGAL:
                return [BankConnectivityProvider.GoCardLess];
            default:
                return null;
        }
    }

    static getBuyersPaymentMethodProvider(buyer?: Buyer): BankConnectivityProvider {
        if (!buyer?.address?.country) {
            throw new ValidationError('No buyer country', 'no_buyer_country');
        }
        return this.getPaymentMethodProvider(buyer.address.country);
    }

    static getPaymentMethodProvider(country: Country): BankConnectivityProvider {
        switch (country) {
            // specific providers per countries will be added here
            case Country.UNITED_KINGDOM:
                return BankConnectivityProvider.GoCardLess;
            case Country.AUSTRALIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.GERMANY:
                return BankConnectivityProvider.GoCardLess;
            case Country.FRANCE:
                return BankConnectivityProvider.GoCardLess;
            case Country.ITALY:
                return BankConnectivityProvider.GoCardLess;
            case Country.SPAIN:
                return BankConnectivityProvider.GoCardLess;
            case Country.BELGIUM:
                return BankConnectivityProvider.GoCardLess;
            case Country.NETHERLANDS:
                return BankConnectivityProvider.GoCardLess;
            case Country.GUADELOUPE:
                return BankConnectivityProvider.GoCardLess;
            case Country.REUNION:
                return BankConnectivityProvider.GoCardLess;
            case Country.MARTINIQUE:
                return BankConnectivityProvider.GoCardLess;
            case Country.SWITZERLAND:
                return BankConnectivityProvider.GoCardLess;
            case Country.CZECH_REPUBLIC:
                return BankConnectivityProvider.GoCardLess;
            case Country.IRELAND:
                return BankConnectivityProvider.GoCardLess;
            case Country.CROATIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.SLOVENIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.SWEDEN:
                return BankConnectivityProvider.GoCardLess;
            case Country.UNITED_STATES:
                return BankConnectivityProvider.Stripe;
            case Country.CANADA:
                return BankConnectivityProvider.Stripe;
            case Country.ROMANIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.GREECE:
                return BankConnectivityProvider.GoCardLess;
            case Country.BULGARIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.LITHUANIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.DENMARK:
                return BankConnectivityProvider.GoCardLess;
            case Country.AUSTRIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.CYPRUS:
                return BankConnectivityProvider.GoCardLess;
            case Country.ESTONIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.FINLAND:
                return BankConnectivityProvider.GoCardLess;
            case Country.HUNGARY:
                return BankConnectivityProvider.GoCardLess;
            case Country.ICELAND:
                return BankConnectivityProvider.GoCardLess;
            case Country.LATVIA:
                return BankConnectivityProvider.GoCardLess;
            case Country.LIECHTENSTEIN:
                return BankConnectivityProvider.GoCardLess;
            case Country.LUXEMBOURG:
                return BankConnectivityProvider.GoCardLess;
            case Country.MALTA:
                return BankConnectivityProvider.GoCardLess;
            case Country.MONACO:
                return BankConnectivityProvider.GoCardLess;
            case Country.NEW_ZEALAND:
                return BankConnectivityProvider.GoCardLess;
            case Country.NORWAY:
                return BankConnectivityProvider.GoCardLess;
            case Country.POLAND:
                return BankConnectivityProvider.GoCardLess;
            case Country.PORTUGAL:
                return BankConnectivityProvider.GoCardLess;
            case Country.SAN_MARINO:
                return BankConnectivityProvider.GoCardLess;
            default:
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore - Fix TS FS-3199
                return null;
        }
    }

    static getCurrencyByCountry(country: Country): Currency {
        const currencyMap = new Map<Country, Currency>([
            [Country.UNITED_KINGDOM, Currency.GBP],
            [Country.AUSTRALIA, Currency.AUD],
            [Country.NEW_ZEALAND, Currency.NZD],
            [Country.CANADA, Currency.CAD],
            [Country.MEXICO, Currency.MXN],
            [Country.HONG_KONG, Currency.HKD],
            [Country.ROMANIA, Currency.RON],
            [Country.UNITED_ARAB_EMIRATES, Currency.AED],
            [Country.DENMARK, Currency.DKK],
            [Country.SWEDEN, Currency.SEK],
            [Country.SINGAPORE, Currency.SGD],
            [Country.POLAND, Currency.PLN],
            [Country.HONG_KONG, Currency.HKD],
            ...[
                Country.GERMANY,
                Country.FRANCE,
                Country.ITALY,
                Country.SPAIN,
                Country.BELGIUM,
                Country.NETHERLANDS,
                Country.GUADELOUPE,
                Country.REUNION,
                Country.MARTINIQUE,
                Country.SWITZERLAND,
                Country.CZECH_REPUBLIC,
                Country.IRELAND,
                Country.CROATIA,
                Country.SLOVENIA,
                Country.SWEDEN,
                Country.ROMANIA,
                Country.GREECE,
                Country.BULGARIA,
                Country.LITHUANIA,
                Country.DENMARK,
                Country.AUSTRIA,
                Country.CYPRUS,
                Country.ESTONIA,
                Country.FINLAND,
                Country.HUNGARY,
                Country.ICELAND,
                Country.LATVIA,
                Country.LIECHTENSTEIN,
                Country.LUXEMBOURG,
                Country.MALTA,
                Country.MONACO,
                Country.NORWAY,
                Country.PORTUGAL,
                Country.SAN_MARINO,
                Country.SLOVAKIA,
                Country.KOSOVO,
                Country.ALBANIA,
                Country.ARMENIA,
                Country.AZERBAIJAN,
                Country.BELARUS,
                Country.BOSNIA_AND_HERZEGOVINA,
                Country.GEORGIA,
                Country.NORTH_MACEDONIA,
                Country.MOLDOVA,
                Country.MONTENEGRO,
                Country.SERBIA,
                Country.TURKEY,
                Country.UKRAINE,
                Country.GIBRALTAR,
            ].map((c) => [c, Currency.EUR] as [Country, Currency]),
        ]);

        return currencyMap.get(country) || Currency.USD;
    }

    static getCreditCurrencyByCountry(country: Country): Currency {
        const currencyMap = new Map<Country, Currency>([
            [Country.UNITED_KINGDOM, Currency.GBP],
            [Country.AUSTRALIA, Currency.AUD],
            [Country.NEW_ZEALAND, Currency.USD],
            [Country.CANADA, Currency.CAD],
            [Country.MEXICO, Currency.USD],
            [Country.HONG_KONG, Currency.USD],
            [Country.ROMANIA, Currency.USD],
            [Country.UNITED_ARAB_EMIRATES, Currency.USD],
            [Country.DENMARK, Currency.USD],
            [Country.SWEDEN, Currency.USD],
            [Country.SINGAPORE, Currency.USD],
            [Country.POLAND, Currency.USD],
            [Country.HONG_KONG, Currency.USD],
            ...[
                Country.GERMANY,
                Country.FRANCE,
                Country.ITALY,
                Country.SPAIN,
                Country.BELGIUM,
                Country.NETHERLANDS,
                Country.GUADELOUPE,
                Country.REUNION,
                Country.MARTINIQUE,
                Country.SWITZERLAND,
                Country.CZECH_REPUBLIC,
                Country.IRELAND,
                Country.CROATIA,
                Country.SLOVENIA,
                Country.SWEDEN,
                Country.ROMANIA,
                Country.GREECE,
                Country.BULGARIA,
                Country.LITHUANIA,
                Country.DENMARK,
                Country.AUSTRIA,
                Country.CYPRUS,
                Country.ESTONIA,
                Country.FINLAND,
                Country.HUNGARY,
                Country.ICELAND,
                Country.LATVIA,
                Country.LIECHTENSTEIN,
                Country.LUXEMBOURG,
                Country.MALTA,
                Country.MONACO,
                Country.NORWAY,
                Country.PORTUGAL,
                Country.SAN_MARINO,
                Country.SLOVAKIA,
                Country.KOSOVO,
                Country.ALBANIA,
                Country.ARMENIA,
                Country.AZERBAIJAN,
                Country.BELARUS,
                Country.BOSNIA_AND_HERZEGOVINA,
                Country.GEORGIA,
                Country.NORTH_MACEDONIA,
                Country.MOLDOVA,
                Country.MONTENEGRO,
                Country.SERBIA,
                Country.TURKEY,
                Country.UKRAINE,
                Country.GIBRALTAR,
            ].map((c) => [c, Currency.EUR] as [Country, Currency]),
        ]);

        return currencyMap.get(country) || Currency.USD;
    }

    static getPaymentMethodTypeByCountry(country: Country): PaymentMethodType {
        const paymentMethodTypeByCountry = this.countryToPaymentMethodTypeMapper.get(country);
        if (paymentMethodTypeByCountry) {
            return paymentMethodTypeByCountry;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore - Fix TS FS-3199
        return undefined;
    }

    static getCurrencyByPaymentMethod(paymentMethodType: PaymentMethodType, newAcss?: boolean): Currency[] {
        switch (paymentMethodType) {
            case PaymentMethodType.BACS:
                return [Currency.GBP];
            case PaymentMethodType.BECS:
                return [Currency.AUD];
            case PaymentMethodType.SEPA:
                return [Currency.EUR];
            case PaymentMethodType.ACSS:
                return [Currency.CAD, Currency.USD];
            default:
                return [Currency.USD];
        }
    }

    static getPaymentMethodByCurrency(currency?: Currency): PaymentMethodType | undefined {
        switch (currency) {
            case Currency.GBP:
                return PaymentMethodType.BACS;
            case Currency.AUD:
                return PaymentMethodType.BECS;
            case Currency.EUR:
                return PaymentMethodType.SEPA;
            case Currency.USD:
                return PaymentMethodType.ACH;
            case Currency.CAD:
                return PaymentMethodType.ACSS;
            default:
                return undefined;
        }
    }

    static getCurrencySymbol(currency?: Currency) {
        switch (currency) {
            case Currency.GBP:
                return '£';
            case Currency.USD:
                return '$';
            case Currency.EUR:
                return '€';
            case Currency.AUD:
                return 'A$';
            case Currency.CAD:
                return 'CA$';
            case Currency.MXN:
                return 'M$';
            case Currency.RON:
                return 'lei';
            case Currency.PLN:
                return 'zł';
            case Currency.NZD:
                return 'NZ$';
            case Currency.JPY:
                return '¥';
            default:
                return '$';
        }
    }

    static hasConnectedBankAccount(financialConnections: FinancialConnection[]): boolean {
        return financialConnections?.some((fc) =>
            [
                FinancialConnectionProviderType.Plaid,
                FinancialConnectionProviderType.GoCardLess,
                FinancialConnectionProviderType.Stripe,
            ].includes(fc.providerType),
        );
    }

    static getPaymentMethodForPayLater(country: Country, paymentMethods: PaymentMethod[]) {
        const methodTypeForPayLater = this.getPaymentMethodTypeByCountry(country);
        return paymentMethods?.find((paymentMethod) => paymentMethod.methodType === methodTypeForPayLater);
    }

    static getVirtualAccountFields(currency?: Currency) {
        if (currency === Currency.MXN) {
            return ['clabeNumber'];
        }

        if (currency && [(Currency.RON, Currency.AED, Currency.SEK, Currency.PLN)].includes(currency)) {
            return ['iban', 'swiftCode'];
        }

        if (currency === Currency.DKK) {
            return ['accountNumber', 'bankCode', 'iban', 'swiftCode'];
        }

        if (currency === Currency.SGD) {
            return ['accountNumber', 'bankCode', 'branchCode', 'swiftCode'];
        }

        return currency !== Currency.CAD
            ? [
                  'accountNumber',
                  'routingNumber',
                  'sortCode',
                  'bsbCode',
                  'iban',
                  'swiftCode',
                  'institutionNumber',
                  'transitNumber',
                  'bankCode',
                  'branchCode',
              ]
            : ['institutionNumber', 'accountNumber', 'routingNumber', 'sortCode', 'bsbCode', 'swiftCode', 'iban', 'transitNumber', 'email'];
    }

    static mapBankAccountDetails(bankAccountDetails: BankAccountDetails[], supplierName: string, reference: string, currency: Currency) {
        const domesticBankAccountDetails = bankAccountDetails.find((b) => b.location === BankAccountLocation.Domestic);
        const internationalBankAccountDetails = bankAccountDetails.find((b) => b.location === BankAccountLocation.International);

        const prettyBankAccountDetails = {
            domesticBankAccountDetails: domesticBankAccountDetails && {
                accountName: { key: 'Account name', value: supplierName },
                reference: { key: 'Reference', value: reference },
                bankName: { key: 'Bank name', value: domesticBankAccountDetails.bankName },
            },
            internationalBankAccountDetails: internationalBankAccountDetails && {
                accountName: { key: 'Account name', value: supplierName },
                reference: { key: 'Reference', value: reference },
                bankName: { key: 'Bank name', value: internationalBankAccountDetails.bankName },
            },
        };

        const keys = this.getVirtualAccountFields(currency);

        keys.forEach((key) => {
            if (prettyBankAccountDetails.domesticBankAccountDetails && domesticBankAccountDetails?.bankAccountPayload?.[key]) {
                prettyBankAccountDetails.domesticBankAccountDetails[key] = {
                    key: this.formatBankAccountField(key),
                    value: domesticBankAccountDetails?.bankAccountPayload?.[key],
                };
            }

            if (prettyBankAccountDetails.internationalBankAccountDetails && internationalBankAccountDetails?.bankAccountPayload?.[key]) {
                prettyBankAccountDetails.internationalBankAccountDetails[key] = {
                    key: this.formatBankAccountField(key),
                    value: internationalBankAccountDetails?.bankAccountPayload?.[key],
                };
            }
        });

        return prettyBankAccountDetails;
    }

    static getDefaultBankAddress(currency?: Currency): Address {
        const defaultBankAddresses = new Map<Currency | undefined, Address>([
            [
                Currency.AUD,
                {
                    country: Country.AUSTRALIA,
                    city: 'Sydney',
                    addressLine1: 'Suite 1, Level 11, 66 Goulburn Street',
                    zipCode: '2000',
                },
            ],
            [
                Currency.EUR,
                {
                    country: Country.BELGIUM,
                    city: 'Brussels',
                    addressLine1: 'Rue du Trône 100',
                    zipCode: '1050',
                },
            ],
            [
                Currency.GBP,
                {
                    country: Country.UNITED_KINGDOM,
                    city: 'London',
                    addressLine1: '56 Shoreditch High Street',
                    zipCode: 'E1 6JJ',
                },
            ],
        ]);

        const bankAddressByCurrency = defaultBankAddresses.get(currency);

        if (bankAddressByCurrency) {
            return bankAddressByCurrency;
        }

        return {
            country: Country.UNITED_STATES,
            city: 'San Francisco',
            addressLine1: '420 Montgomery St.',
            state: 'CA',
            zipCode: '94104',
        };
    }

    static formatBankAccountField(field: string): string {
        if (field === 'iban') return 'IBAN';
        if (field === 'bsbCode') return 'BSB Code';
        if (field === 'clabeNumber') return 'CLABE Number';

        return startCase(field);
    }

    static getBankCountryByPaymentMethod(paymentMethod: PaymentMethod): Country | undefined {
        if (paymentMethod.methodType === PaymentMethodType.ACH) {
            return Country.UNITED_STATES;
        }
        if (paymentMethod.methodType === PaymentMethodType.ACSS) {
            return Country.CANADA;
        }
        if (paymentMethod.methodType === PaymentMethodType.BACS) {
            return Country.UNITED_KINGDOM;
        }
        if (paymentMethod.methodType === PaymentMethodType.BECS) {
            return (paymentMethod.methodTypeFields as BECSPaymentMethod).country;
        }
        if (paymentMethod.methodType === PaymentMethodType.SEPA) {
            return (paymentMethod.methodTypeFields as SEPAPaymentMethod).country;
        }
    }
}
